import { useEffect, useContext, useRef } from "react";
import { DataContext } from "../context/DataContext";

const useEdgeStatusCheck = (triggerCondition, customHandler = null) => {
  const interval = 2000; // 2 seconds polling interval
  const { settings, dispatch } = useContext(DataContext);
  const isRequestPendingRef = useRef(false); // Track if a request is in progress

  useEffect(() => {
    if (!triggerCondition) return;

    const requestBody = {
      request_id: settings.request_id,
    };

    const handleStatusResponse = (result) => {
      if (!result || !result.status) return true; // Continue polling if no result

      // For edge mode, we need to let the custom handler manage the state
      // The handler will determine when to show form vs when to complete
      if (customHandler) {
        const shouldContinue = customHandler(result, false); // No confirmation tracking in edge mode
        
        // If handler returns false, stop polling
        if (shouldContinue === false) {
          return false;
        }
        
        // For terminal states, also stop polling
        if (result.status === "PASS" || result.status === "FAIL") {
          return false;
        }
      } else {
        // Fallback handling if no custom handler
        if (result.status === "PASS") {
          dispatch({ type: "SET_STATUS", payload: "PASS" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
          return false; // Stop polling
        } else if (result.status === "FAIL") {
          dispatch({ type: "SET_STATUS", payload: "FAIL" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
          return false; // Stop polling
        }
      }

      return true; // Continue polling by default (PENDING state)
    };

    const fetchStatusCheck = async () => {
      // Skip this request if another one is already in progress
      if (isRequestPendingRef.current) {
        return;
      }
      
      try {
        // Mark that we're starting a request
        isRequestPendingRef.current = true;
        
        const response = await fetch(
          process.env.REACT_APP_EDGE_STATUS_URL,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch status");
        }

        const result = await response.json();
        const shouldContinue = handleStatusResponse(result);
        
        if (!shouldContinue) {
          clearInterval(intervalId);
        }
      } catch (error) {
        // Continue silently without breaking the flow
        console.error("Edge status check error:", error);
      } finally {
        // Mark that the request has completed
        isRequestPendingRef.current = false;
      }
    };

    // Initial fetch with delay
    let timeoutId = setTimeout(() => {
      fetchStatusCheck();
      
      // Then set up the interval for subsequent fetches
      intervalId = setInterval(fetchStatusCheck, interval);
    }, interval); // Wait 2 seconds before first fetch
    
    let intervalId;

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      // Reset pending flag when unmounting
      isRequestPendingRef.current = false;
    };
  }, [
    triggerCondition,
    settings.request_id,
    dispatch,
    customHandler,
  ]);
};

export default useEdgeStatusCheck;